import { useState } from "react";
import styled from "styled-components";

import { Chip, Chips, ErrorText } from "../../../components/PartnerDashboard/ui";
import {
  DATE_PRESETS,
  DatePreset,
  DateRange,
  addDays,
  formatRangeDate,
  presetRange,
} from "../../../utils/reportDates";

const DateInput = styled.input`
  font-family: 'Aileron', 'Poppins', sans-serif;
  font-size: 13px;
  padding: 7px 10px;
  border: 1.5px solid #e0d8ce;
  border-radius: 10px;
  background: #ffffff;
  min-width: 0;
  -webkit-appearance: none;
  appearance: none;
  min-height: 36px;
  color: #111111;
  text-align: left;

  &::-webkit-date-and-time-value {
    text-align: left;
  }

  @media (max-width: 700px) {
    font-size: 16px;
    flex: 1;
  }
`;

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-family: 'Aileron', 'Poppins', sans-serif;
  font-size: 13px;
  color: #6b6259;
  flex-wrap: wrap;
`;

const PRESETS = DATE_PRESETS.filter((p) => p.key !== "all");

export const useRange = (initial: DatePreset) => {
  const [preset, setPreset] = useState<DatePreset>(initial);
  const [custom, setCustom] = useState<DateRange>(() => presetRange("last30")!);
  const range = preset === "custom" ? custom : presetRange(preset)!;
  const invalid = preset === "custom" && (!custom.start || !custom.end || custom.start > custom.end);
  return {
    preset,
    setPreset,
    custom,
    setCustom,
    range,
    invalid,
    endExclusive: invalid ? "" : addDays(range.end, 1),
    label: invalid ? "" : `${formatRangeDate(range.start)} – ${formatRangeDate(range.end)}`,
  };
};

type RangeState = ReturnType<typeof useRange>;

const RangePicker = ({ state }: { state: RangeState }) => (
  <>
    <Chips>
      {PRESETS.map((p) => (
        <Chip key={p.key} $active={state.preset === p.key} onClick={() => state.setPreset(p.key)}>
          {p.label}
        </Chip>
      ))}
    </Chips>
    {state.preset === "custom" && (
      <DateRow>
        <DateInput
          type="date"
          aria-label="Start date"
          value={state.custom.start}
          max={state.custom.end}
          onChange={(e) => state.setCustom((c) => ({ ...c, start: e.target.value }))}
        />
        to
        <DateInput
          type="date"
          aria-label="End date"
          value={state.custom.end}
          min={state.custom.start}
          onChange={(e) => state.setCustom((c) => ({ ...c, end: e.target.value }))}
        />
      </DateRow>
    )}
    {state.invalid && <ErrorText>Choose a start date on or before the end date.</ErrorText>}
  </>
);

export default RangePicker;
